"use client";

import { useState } from "react";
import { AnimatedIcon } from "@/components/ui/animated-icon";
import { cn } from "@/lib/utils";
import type { VisualRef } from "@/types/modules";

export const LNDEV_LOGO_BASE = "https://cdn.handyout.com/lndev-logos/logos";

export function resolveLndevSlug(slug: string): string {
  const clean = slug.startsWith("lndev:")
    ? slug.slice("lndev:".length)
    : slug;
  return `${LNDEV_LOGO_BASE}/${clean.trim().toLowerCase()}.svg`;
}

interface RemoteImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackText?: string;
  fallbackClassName?: string;
}

export function RemoteImage({
  src,
  alt,
  className,
  fallbackText,
  fallbackClassName,
}: RemoteImageProps) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div
        className={cn(
          "grid place-items-center rounded bg-black/[0.06] text-[10px] font-medium tabular-nums text-[var(--subtle)]",
          fallbackClassName,
        )}
      >
        {(fallbackText ?? alt).slice(0, 2).toUpperCase()}
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      draggable={false}
      onError={() => setFailed(true)}
      className={cn("object-contain", className)}
    />
  );
}

interface VisualRefRenderProps {
  visual: VisualRef;
  className?: string;
  alt?: string;
}

export function VisualRefRender({
  visual,
  className,
  alt,
}: VisualRefRenderProps) {
  switch (visual.type) {
    case "icon":
      return (
        <AnimatedIcon name={visual.name} className={cn("shrink-0", className)} />
      );
    case "image":
      return (
        <RemoteImage
          src={visual.src}
          alt={alt ?? ""}
          className={cn("shrink-0", className)}
        />
      );
    case "logo":
      return (
        <RemoteImage
          src={resolveLndevSlug(visual.slug)}
          alt={alt ?? visual.slug.replace(/^lndev:/, "")}
          className={cn("shrink-0", className)}
          fallbackText={visual.slug.replace(/^lndev:/, "").slice(0, 2)}
        />
      );
    case "number":
      return (
        <span
          className={cn(
            "inline-flex shrink-0 items-center justify-center font-medium tabular-nums leading-none",
            className,
          )}
        >
          {visual.value}
        </span>
      );
  }
}
